import type { CountryLocationGroup, ServiceabilityLocation } from "./types";

export interface DisplayChip {
	id: number;
	label: string;
}

export const getAllDescendantIds = (node: ServiceabilityLocation): number[] => {
	const ids = [node.id];
	node.children?.forEach((child) => {
		ids.push(...getAllDescendantIds(child));
	});
	return ids;
};

export const buildLocationMap = (
	groups: CountryLocationGroup[],
): Map<number, ServiceabilityLocation> => {
	const map = new Map<number, ServiceabilityLocation>();
	const walk = (node: ServiceabilityLocation) => {
		map.set(node.id, node);
		node.children?.forEach(walk);
	};
	groups.forEach((g) => g.locations.forEach(walk));
	return map;
};

export const buildDisplayChips = (
	selectedIds: Set<number>,
	tree: CountryLocationGroup[],
	showCode = false,
): DisplayChip[] => {
	const chips: DisplayChip[] = [];

	const isFullySelected = (node: ServiceabilityLocation): boolean => {
		if (!selectedIds.has(node.id)) return false;
		return !node.children?.length || node.children.every(isFullySelected);
	};

	const walkForChips = (
		node: ServiceabilityLocation,
		countryName: string,
		isRoot: boolean,
	) => {
		if (isFullySelected(node)) {
			const childLabel = showCode && node.code ? node.code : node.name;
			chips.push({
				id: node.id,
				label: isRoot ? `${node.name} - ${countryName}` : childLabel,
			});
			return;
		}

		node.children?.forEach((child) =>
			walkForChips(child, countryName, false),
		);
	};

	tree.forEach((group) => {
		group.locations.forEach((root) =>
			walkForChips(root, group.country.name, true),
		);
	});

	return chips;
};
